import { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Save, X, ClipboardPaste, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  template_type: string;
  created_at: string;
}

const TEMPLATE_CATEGORIES = [
  {
    group: 'By Purpose',
    items: [
      { value: 'outreach', label: 'Initial Outreach' },
      { value: 'follow_up', label: 'Follow Up' },
      { value: 'intro', label: 'Introduction' },
      { value: 'proposal', label: 'Proposal' },
      { value: 'thank_you', label: 'Thank You' },
    ],
  },
  {
    group: 'By Location Type',
    items: [
      { value: 'hotel', label: 'Hotel Lobbies' },
      { value: 'cafe', label: 'Cafes' },
      { value: 'train_station', label: 'Train Stations' },
      { value: 'airport', label: 'Airports' },
      { value: 'library', label: 'Libraries' },
      { value: 'coworking', label: 'Coworking Spaces' },
      { value: 'gym', label: 'Gyms' },
      { value: 'shopping_centre', label: 'Shopping Centres' },
      { value: 'university', label: 'Universities' },
      { value: 'office', label: 'Office Buildings' },
    ],
  },
];

const ALL_TYPES = TEMPLATE_CATEGORIES.flatMap((g) => g.items);

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  const pasteBodyRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({ name: '', subject: '', body: '', template_type: 'outreach' });
  const [pasteForm, setPasteForm] = useState({ name: '', subject: '', template_type: 'outreach' });

  const fetchTemplates = async () => {
    const { data } = await supabase
      .from('email_templates')
      .select('*')
      .order('template_type')
      .order('created_at', { ascending: false });
    if (data) setTemplates(data);
    setLoading(false);
  };

  useEffect(() => { fetchTemplates(); }, []);

  const resetForm = () => {
    setForm({ name: '', subject: '', body: '', template_type: 'outreach' });
    setEditingId(null);
    setShowAdd(false);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error('All fields are required');
      return;
    }

    if (editingId) {
      const { error } = await supabase.from('email_templates').update({
        name: form.name.trim(),
        subject: form.subject.trim(),
        body: form.body.trim(),
        template_type: form.template_type,
      }).eq('id', editingId);

      if (error) {
        toast.error('Failed to update template');
      } else {
        toast.success('Template updated');
        resetForm();
        fetchTemplates();
      }
    } else {
      const { error } = await supabase.from('email_templates').insert({
        name: form.name.trim(),
        subject: form.subject.trim(),
        body: form.body.trim(),
        template_type: form.template_type,
      });

      if (error) {
        toast.error('Failed to create template');
      } else {
        toast.success('Template created');
        resetForm();
        fetchTemplates();
      }
    }
  };

  const handlePasteSave = async () => {
    const html = pasteBodyRef.current?.innerHTML || '';
    if (!pasteForm.name.trim() || !pasteForm.subject.trim() || !html.trim()) {
      toast.error('All fields are required');
      return;
    }

    const { error } = await supabase.from('email_templates').insert({
      name: pasteForm.name.trim(),
      subject: pasteForm.subject.trim(),
      body: html,
      template_type: pasteForm.template_type,
    });

    if (error) {
      toast.error('Failed to create template');
    } else {
      toast.success('Template added from paste');
      setPasteForm({ name: '', subject: '', template_type: 'outreach' });
      if (pasteBodyRef.current) pasteBodyRef.current.innerHTML = '';
      setShowPaste(false);
      fetchTemplates();
    }
  };

  const handleEdit = (t: EmailTemplate) => {
    setForm({ name: t.name, subject: t.subject, body: t.body, template_type: t.template_type });
    setEditingId(t.id);
    setShowAdd(true);
    setShowPaste(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('email_templates').delete().eq('id', id);
    if (error) {
      toast.error('Failed to delete template');
    } else {
      toast.success('Template deleted');
      fetchTemplates();
    }
  };

  const handleCopyTemplate = (t: EmailTemplate) => {
    // If body contains HTML, render it properly for clipboard
    const isHtml = /<[a-z][\s\S]*>/i.test(t.body);
    if (isHtml) {
      const blob = new Blob([t.body], { type: 'text/html' });
      const plainBlob = new Blob([t.body.replace(/<[^>]*>/g, '')], { type: 'text/plain' });
      navigator.clipboard.write([
        new ClipboardItem({
          'text/html': blob,
          'text/plain': plainBlob,
        }),
      ]).then(() => toast.success('Copied with formatting'));
    } else {
      navigator.clipboard.writeText(t.body).then(() => toast.success('Copied to clipboard'));
    }
  };

  const filtered = filterType ? templates.filter((t) => t.template_type === filterType) : templates;

  const getTypeLabel = (val: string) => ALL_TYPES.find((t) => t.value === val)?.label || val;

  const TYPE_COLORS: Record<string, string> = {
    outreach: 'bg-blue-100 text-blue-700',
    follow_up: 'bg-amber-100 text-amber-700',
    intro: 'bg-green-100 text-green-700',
    proposal: 'bg-purple-100 text-purple-700',
    thank_you: 'bg-pink-100 text-pink-700',
    hotel: 'bg-sky-100 text-sky-700',
    cafe: 'bg-orange-100 text-orange-700',
    train_station: 'bg-cyan-100 text-cyan-700',
    airport: 'bg-teal-100 text-teal-700',
    library: 'bg-indigo-100 text-indigo-700',
    coworking: 'bg-violet-100 text-violet-700',
    gym: 'bg-rose-100 text-rose-700',
    shopping_centre: 'bg-fuchsia-100 text-fuchsia-700',
    university: 'bg-lime-100 text-lime-700',
    office: 'bg-slate-100 text-slate-700',
  };

  const getTypeColor = (val: string) => TYPE_COLORS[val] || 'bg-muted text-muted-foreground';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Email Templates</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage reusable templates. Use {'{{contact_name}}'} and {'{{location_name}}'} as placeholders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setShowPaste(true); setShowAdd(false); }}>
            <ClipboardPaste className="w-4 h-4 mr-1" /> Paste Email
          </Button>
          <Button onClick={() => { resetForm(); setShowAdd(true); setShowPaste(false); }}>
            <Plus className="w-4 h-4 mr-1" /> New Template
          </Button>
        </div>
      </div>

      {/* Category filter */}
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setFilterType('')}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              !filterType ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </button>
          <span className="text-[10px] text-muted-foreground mx-1 uppercase tracking-wide font-medium">Purpose:</span>
          {TEMPLATE_CATEGORIES[0].items.map((t) => (
            <button
              key={t.value}
              onClick={() => setFilterType(filterType === t.value ? '' : t.value)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                filterType === t.value ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] text-muted-foreground mr-1 uppercase tracking-wide font-medium">Location:</span>
          {TEMPLATE_CATEGORIES[1].items.map((t) => (
            <button
              key={t.value}
              onClick={() => setFilterType(filterType === t.value ? '' : t.value)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                filterType === t.value ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Paste Email form */}
      {showPaste && (
        <div className="bg-card border border-border rounded-xl p-4 mb-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-foreground">Paste an email</h3>
            <button onClick={() => setShowPaste(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Paste your email below with formatting (bold, links, etc.) preserved. Ctrl+V / Cmd+V from your email client.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Template Name</label>
              <input
                value={pasteForm.name}
                onChange={(e) => setPasteForm({ ...pasteForm, name: e.target.value })}
                placeholder="e.g. Hotel warm follow-up"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
              <select
                value={pasteForm.template_type}
                onChange={(e) => setPasteForm({ ...pasteForm, template_type: e.target.value })}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              >
                {TEMPLATE_CATEGORIES.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.items.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject Line</label>
            <input
              value={pasteForm.subject}
              onChange={(e) => setPasteForm({ ...pasteForm, subject: e.target.value })}
              placeholder="e.g. Partnership opportunity - qwip privacy pods"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Email Body (paste here)</label>
            <div
              ref={pasteBodyRef}
              contentEditable
              className="w-full border border-border rounded-lg px-3 py-3 text-sm bg-background text-foreground min-h-[200px] max-h-[400px] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-primary/20 prose prose-sm max-w-none"
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handlePasteSave}>
              <Save className="w-4 h-4 mr-1" /> Save Template
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit form (plain text) */}
      {showAdd && (
        <div className="bg-card border border-border rounded-xl p-4 mb-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-foreground">
              {editingId ? 'Edit Template' : 'New Template'}
            </h3>
            <button onClick={resetForm} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Warm Hotel Outreach"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
              <select
                value={form.template_type}
                onChange={(e) => setForm({ ...form, template_type: e.target.value })}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              >
                {TEMPLATE_CATEGORIES.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.items.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject Line</label>
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="e.g. Partnership opportunity - qwip privacy pods at {{location_name}}"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Body</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={10}
              placeholder={"Hi {{contact_name}},\n\nI'm reaching out regarding..."}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground resize-y font-mono"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave}>
              <Save className="w-4 h-4 mr-1" />
              {editingId ? 'Update' : 'Save Template'}
            </Button>
          </div>
        </div>
      )}

      {/* Template list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {templates.length === 0
            ? 'No templates yet. Paste in an email or create one to get started.'
            : 'No templates match this filter.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((t) => {
            const isExpanded = expandedId === t.id;
            return (
              <div key={t.id} className="bg-card border border-border rounded-xl p-4">
                <div
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : t.id)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <h4 className="font-semibold text-sm text-foreground truncate">{t.name}</h4>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${getTypeColor(t.template_type)}`}>
                      {getTypeLabel(t.template_type)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCopyTemplate(t); }}
                      className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted"
                      title="Copy with formatting"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleEdit(t); }} className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }} className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-muted">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-2">
                  <span className="font-medium">Subject:</span> {t.subject}
                </p>

                {!isExpanded && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">
                    {t.body.replace(/<[^>]*>/g, '').slice(0, 120)}...
                  </p>
                )}

                {isExpanded && (
                  <div className="mt-3 border-t border-border pt-3">
                    <div
                      className="prose prose-sm max-w-none text-sm text-foreground"
                      dangerouslySetInnerHTML={{ __html: /<[a-z][\s\S]*>/i.test(t.body) ? t.body : `<pre class="whitespace-pre-wrap font-sans text-sm">${t.body}</pre>` }}
                    />
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCopyTemplate(t); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy Email
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesPage;
